import {
  getInstance,
  registerGlobalPlugins,
  registerPlugins,
  type ModuleFederationRuntimePlugin,
} from '@module-federation/enhanced/runtime';
import type {
  RemoteEntryExports,
  RemoteInfo,
} from '@module-federation/runtime-core/types';

export const API_REMOTE_ENTRY_MARKER = '__exoSkeletonApiOwned' as const;
const API_REMOTE_ENTRY_TIMEOUT_PLUGIN_NAME =
  'exo-skeleton-api-remote-entry-timeout';

const isApiRemoteEntryPluginAlreadyRegistered = () => {
  const globalPlugins = (
    globalThis as typeof globalThis & {
      __FEDERATION__?: {
        __GLOBAL_PLUGIN__?: Array<{ name?: string }>;
      };
    }
  ).__FEDERATION__?.__GLOBAL_PLUGIN__;

  return (
    Array.isArray(globalPlugins) &&
    globalPlugins.some(
      (plugin) => plugin?.name === API_REMOTE_ENTRY_TIMEOUT_PLUGIN_NAME,
    )
  );
};

type ApiOwnedRemote = RemoteInfo & {
  [API_REMOTE_ENTRY_MARKER]: true;
};

type LoadEntryArgs = Parameters<
  NonNullable<ModuleFederationRuntimePlugin['loadEntry']>
>[0];

const isApiOwnedRemote = (remote: RemoteInfo): remote is ApiOwnedRemote =>
  (
    remote as RemoteInfo & Partial<Record<typeof API_REMOTE_ENTRY_MARKER, true>>
  )[API_REMOTE_ENTRY_MARKER] === true;

const isRemoteEntryExports = (value: unknown): value is RemoteEntryExports => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<RemoteEntryExports>;
  return (
    typeof candidate.get === 'function' && typeof candidate.init === 'function'
  );
};

const getRemoteEntryExports = (globalName: string) =>
  (globalThis as unknown as Record<string, unknown>)[globalName];

const toAbsoluteUrl = (url: string) => {
  try {
    return new URL(url, document.baseURI).href;
  } catch {
    return url;
  }
};

const hasSameUrl = (script: HTMLScriptElement, url: string) =>
  toAbsoluteUrl(script.getAttribute('src') || script.src) ===
  toAbsoluteUrl(url);

const findExistingScript = (url: string) =>
  Array.from(document.scripts).find((script) => hasSameUrl(script, url));

const createScriptNetworkError = (url: string) => {
  const networkError = new Error(
    `ScriptNetworkError: Failed to load script "${url}" - the script URL is unreachable or the server returned an error (network failure, 404, CORS, etc.)`,
  );
  networkError.name = 'ScriptNetworkError';
  return networkError;
};

const createMissingContainerError = (url: string, globalName: string) =>
  new Error(
    `Remote entry "${url}" did not expose a container on "${globalName}"`,
  );

const createScriptExecutionError = (url: string, event: ErrorEvent) => {
  const executionError = new Error(
    // eslint-disable-next-line spellcheck/spell-checker -- DOM ErrorEvent fields
    `ScriptExecutionError: Script "${url}" loaded but threw a runtime error during execution: ${event.message} (${event.filename}:${event.lineno}:${event.colno})`,
  );
  executionError.name = 'ScriptExecutionError';
  return executionError;
};

const loadRemoteEntryWithoutTimeout = async (
  args: LoadEntryArgs & { resourceContext?: unknown },
): Promise<RemoteEntryExports> => {
  const { remoteInfo, loaderHook, resourceContext } = args;
  if (!isApiOwnedRemote(remoteInfo) || remoteInfo.type !== 'global') {
    return undefined as never;
  }

  const { entry, entryGlobalName: globalName } = remoteInfo;
  const existingExports = getRemoteEntryExports(globalName);
  if (isRemoteEntryExports(existingExports)) {
    return existingExports;
  }

  if (typeof document === 'undefined') {
    return undefined as never;
  }

  return new Promise<RemoteEntryExports>((resolve, reject) => {
    const url = entry;
    const existingScript = findExistingScript(url);
    let script = existingScript;
    let removeScript = false;
    let executionError: Error | undefined;
    let settled = false;
    const onWindowError = (event: ErrorEvent) => {
      if (
        event.filename &&
        toAbsoluteUrl(event.filename) === toAbsoluteUrl(url)
      ) {
        executionError = createScriptExecutionError(url, event);
      }
    };

    // These handlers close over settle, while cleanup closes over the handlers.
    // Keep the bindings immutable and scope the ordering exception to this cycle.
    /* eslint-disable @typescript-eslint/no-use-before-define -- handler bindings form a cleanup cycle */
    const cleanup = () => {
      window.removeEventListener('error', onWindowError);
      script?.removeEventListener('load', onLoad);
      script?.removeEventListener('error', onError);
      if (removeScript && script?.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
    /* eslint-enable @typescript-eslint/no-use-before-define */

    const settle = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      callback();
    };

    const onLoad = () => {
      settle(() => {
        if (executionError) {
          reject(executionError);
          return;
        }

        const remoteEntryExports = getRemoteEntryExports(globalName);
        if (!isRemoteEntryExports(remoteEntryExports)) {
          reject(createMissingContainerError(url, globalName));
          return;
        }

        resolve(remoteEntryExports);
      });
    };

    const onError = () => {
      settle(() => reject(createScriptNetworkError(url)));
    };

    window.addEventListener('error', onWindowError);

    try {
      if (!script) {
        // eslint-disable-next-line spellcheck/spell-checker -- HTML attribute name
        const attrs = { fetchpriority: 'high' };
        const createScriptArgs = {
          url,
          attrs,
          remoteInfo,
          resourceContext,
        };
        const createScriptResult =
          loaderHook.lifecycle.createScript.emit(createScriptArgs);

        if (
          typeof HTMLScriptElement !== 'undefined' &&
          createScriptResult instanceof HTMLScriptElement
        ) {
          script = createScriptResult;
          removeScript = true;
        } else if (
          createScriptResult &&
          typeof createScriptResult === 'object' &&
          'script' in createScriptResult &&
          createScriptResult.script
        ) {
          script = createScriptResult.script;
          removeScript = true;
        } else {
          const createdScript = document.createElement('script');
          createdScript.type = 'text/javascript';
          Object.entries(attrs).forEach(([name, value]) => {
            if (!createdScript.getAttribute(name)) {
              createdScript.setAttribute(name, value);
            }
          });
          script = createdScript;
          removeScript = true;
        }

        if (!script!.src) {
          script!.src = url;
        }
      }

      const activeScript = script;
      if (!activeScript) {
        throw new Error(`Failed to create script for remote entry "${url}"`);
      }

      activeScript.addEventListener('load', onLoad, { once: true });
      activeScript.addEventListener('error', onError, { once: true });

      if (removeScript) {
        document.head.appendChild(activeScript);
      }
    } catch (error) {
      settle(() => reject(error));
    }
  });
};

export function createApiRemoteEntryTimeoutPlugin(): ModuleFederationRuntimePlugin {
  const plugin: ModuleFederationRuntimePlugin = {
    name: API_REMOTE_ENTRY_TIMEOUT_PLUGIN_NAME,
    loadEntry: loadRemoteEntryWithoutTimeout as NonNullable<
      ModuleFederationRuntimePlugin['loadEntry']
    >,
  };

  return plugin;
}

export function registerApiRemoteEntryTimeoutPlugin(): void {
  const plugin = createApiRemoteEntryTimeoutPlugin();

  if (!isApiRemoteEntryPluginAlreadyRegistered()) {
    registerGlobalPlugins([plugin]);
  }

  const instance = getInstance();
  if (instance) {
    registerPlugins([plugin]);
  }
}
